import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Button,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

const TransactionDetailsModal = ({
  isOpen,
  onClose,
  customer,
  fetchCustomerTransactions,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch transactions when modal opens
  useEffect(() => {
    const loadTransactions = async () => {
      setLoading(true);
      try {
        const data = await fetchCustomerTransactions(customer.id);
        const sortedData = (data || [])
          .filter(transaction =>
            transaction.transactionDetails?.type !== "cancelled"
          )
          .sort((a, b) => {
            return new Date(b.createdAt) - new Date(a.createdAt);
          });

        setTransactions(sortedData);
      } catch (error) {
        console.error("Failed to load transactions:", error);
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen && customer?.id) {
      loadTransactions();
    }
  }, [isOpen, customer?.id, fetchCustomerTransactions]);


  // Format date for display
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return "-";
    return dateStr;
  };

  // Helper to calculate total from advance payment breakup object
  // const calculateAdvanceBreakupTotal = (breakup) => {
  //   if (!breakup || typeof breakup !== 'object') return 0;

  //   return Object.values(breakup).reduce((total, value) => {
  //     const numValue = parseFloat(value) || 0;
  //     return total + numValue;
  //   }, 0);
  // };

  // Get all payment method keys including advance payment breakup
  const getAllPaymentMethods = (transaction) => {
    const methods = new Set(['cash', 'online', 'cheque', 'other']);
    console.log(Array.from(methods));
    // Add advance payment breakup methods
    // if (transaction.paymentDetails?.advancePayment?.breakup) {
    //   Object.keys(transaction.paymentDetails.advancePayment.breakup).forEach(method => {
    //     methods.add(method.toLowerCase());
    //   });
    // }

    return Array.from(methods);
  };

  // Calculate payment amount by method
  const getPaymentAmountByMethod = (transaction, method) => {
    let amount = 0;

    // Regular payment methods
    if (transaction.paymentDetails?.[method]) {
      amount += parseFloat(transaction.paymentDetails[method]) || 0;
    }

    // Advance payment breakup
    // if (transaction.paymentDetails?.advancePayment?.breakup?.[method]) {
    //   amount += parseFloat(transaction.paymentDetails.advancePayment.breakup[method]) || 0;
    // }

    return amount;
  };

  // Calculate totals including advance payments
  const calculateTotals = () => {
    let totalGrand = 0, totalPaid = 0, totalPending = 0, totalDiscount = 0;
    let totalAdvance = 0;

    transactions.forEach((t) => {
      totalGrand += t.summary?.grandTotal || 0;
      totalPaid += t.summary?.paymentDone || 0;
      totalPending += t.summary?.pendingAmount || 0;
      totalDiscount += t.paymentDetails?.discount;

      // Add advance payment total
      totalAdvance += t.paymentDetails?.advancePayment?.total || 0;
    });

    return { totalGrand, totalPaid, totalPending, totalDiscount, totalAdvance };
  };

  const { totalGrand, totalPaid, totalPending, totalDiscount, totalAdvance } = calculateTotals();

  if (!isOpen) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "10px",
          width: "900px",
          maxWidth: "90%",
          maxHeight: "90vh",
          overflow: "hidden",
          backgroundColor: isDark ? "#1e1e1e" : "#ffffff",
          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: "25px",
          pb: "20px",
          borderBottom: isDark ? "1px solid #333" : "none",
        }}
      >
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            color: isDark ? "#e0e0e0" : "#2d3748",
          }}
        >
          Transactions - {customer?.name || "Customer"}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: isDark ? "#a0a0a0" : "#6b7280",
            fontSize: "24px",
            "&:hover": {
              color: isDark ? "#fff" : "#000",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: "25px", pt: "20px !important", overflowY: "auto" }}>
        {loading ? (
          <Box sx={{ textAlign: "center", py: 4, color: isDark ? "#a0a0a0" : "#6b7280" }}>
            <CircularProgress size={40} sx={{ color: "#2563eb", mb: 2 }} />
            <Typography>Loading transactions...</Typography>
          </Box>
        ) : transactions.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4, color: isDark ? "#a0a0a0" : "#6b7280" }}>
            No transactions found.
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "20px",
                justifyContent: "center",
                mb: "25px",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              {/* Total Payment */}
              <Box
                sx={{
                  flex: "1 1",
                  background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                  color: "white",
                  p: "18px",
                  borderRadius: "12px",
                  textAlign: "center",
                  boxShadow: "0 4px 10px rgba(37, 99, 235, 0.3)",
                }}
              >
                <Typography sx={{ fontSize: "13px", opacity: 0.9 }}>Total Payment</Typography>
                <Typography sx={{ fontSize: "22px", fontWeight: 600, mt: "5px" }}>
                  ₹{totalGrand.toLocaleString()}
                </Typography>
              </Box>

              {/* Total Discount */}
              <Box
                sx={{
                  flex: "1 1",
                  background: "linear-gradient(90deg, rgba(174, 181, 36, 1) 0%, rgba(193, 199, 87, 1) 50%, rgba(237, 221, 83, 1) 100%)",
                  color: "white",
                  p: "18px",
                  borderRadius: "12px",
                  textAlign: "center",
                  boxShadow: "0 4px 10px rgba(174, 181, 36, 0.3)",
                }}
              >
                <Typography sx={{ fontSize: "13px", opacity: 0.9 }}>Total Discount</Typography>
                <Typography sx={{ fontSize: "22px", fontWeight: 600, mt: "5px" }}>
                  ₹{totalDiscount.toLocaleString()}
                </Typography>
              </Box>

              {/* Advance Payment */}
              {totalAdvance > 0 && (
                <Box
                  sx={{
                    flex: "1 1",
                    background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
                    color: "white",
                    p: "18px",
                    borderRadius: "12px",
                    textAlign: "center",
                    boxShadow: "0 4px 10px rgba(139, 92, 246, 0.3)",
                  }}
                >
                  <Typography sx={{ fontSize: "13px", opacity: 0.9 }}>Advance Payment</Typography>
                  <Typography sx={{ fontSize: "22px", fontWeight: 600, mt: "5px" }}>
                    ₹{totalAdvance.toLocaleString()}
                  </Typography>
                </Box>
              )}

              {/* Paid Amount */}
              <Box
                sx={{
                  flex: "1 1",
                  background: "linear-gradient(135deg, #059669, #047857)",
                  color: "white",
                  p: "18px",
                  borderRadius: "12px",
                  textAlign: "center",
                  boxShadow: "0 4px 10px rgba(5, 150, 105, 0.3)",
                }}
              >
                <Typography sx={{ fontSize: "13px", opacity: 0.9 }}>Paid Amount</Typography>
                <Typography sx={{ fontSize: "22px", fontWeight: 600, mt: "5px" }}>
                  ₹{totalPaid.toLocaleString()}
                </Typography>
              </Box>

              {/* Pending Amount */}
              <Box
                sx={{
                  flex: "1 1",
                  background: "linear-gradient(135deg, #ef4444, #dc2626)",
                  color: "white",
                  p: "18px",
                  borderRadius: "12px",
                  textAlign: "center",
                  boxShadow: "0 4px 10px rgba(239, 68, 68, 0.3)",
                }}
              >
                <Typography sx={{ fontSize: "13px", opacity: 0.9 }}>Pending Amount</Typography>
                <Typography sx={{ fontSize: "22px", fontWeight: 600, mt: "5px" }}>
                  ₹{totalPending.toLocaleString()}
                </Typography>
              </Box>
            </Box>

            {/* Transaction Cards */}
            {transactions.map((t, index) => {
              const advancePayment = t.paymentDetails?.advancePayment;
              const hasAdvancePayment = advancePayment?.enabled && advancePayment?.total > 0;

              return (
                <Box
                  key={t.id || index}
                  sx={{
                    background: isDark ? "#2a2a2a" : "#ffffff",
                    border: isDark ? "1px solid #444" : "1px solid #e5e7eb",
                    borderRadius: "12px",
                    mb: "20px",
                    p: "20px",
                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  {/* Card Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderBottom: isDark ? "1px solid #444" : "1px solid #f1f5f9",
                      pb: "8px",
                      mb: "12px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "18px",
                        color: isDark ? "#e0e0e0" : "#1e293b",
                        fontWeight: 600,
                      }}
                    >
                      Bill #{t.transactionDetails?.billNo || "-"}
                    </Typography>
                    <Typography sx={{ fontSize: "14px", color: isDark ? "#a0a0a0" : "#6b7280" }}>
                      {formatDateForDisplay(t.date)}
                    </Typography>
                  </Box>

                  {/* Stats Grid */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                      gap: "12px",
                      mb: "15px",
                    }}
                  >
                    <Box
                      sx={{
                        background: isDark ? "#333" : "#f9fafb",
                        p: "12px",
                        borderRadius: "8px",
                        textAlign: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: "12px", color: isDark ? "#a0a0a0" : "#64748b" }}>
                        Total Items
                      </Typography>
                      <Typography
                        sx={{ fontSize: "16px", fontWeight: 600, color: isDark ? "#e0e0e0" : "#1e293b" }}
                      >
                        {t.calculatedTotals?.totalItems || t.summary?.totalItems || 0}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        background: isDark ? "#333" : "#f9fafb",
                        p: "12px",
                        borderRadius: "8px",
                        textAlign: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: "12px", color: isDark ? "#a0a0a0" : "#64748b" }}>
                        Grand Total
                      </Typography>
                      <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#1e40af" }}>
                        ₹{(t.summary?.grandTotal || 0).toLocaleString()}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        background: isDark ? "#333" : "#f9fafb",
                        p: "12px",
                        borderRadius: "8px",
                        textAlign: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: "12px", color: isDark ? "#a0a0a0" : "#64748b" }}>
                        Paid
                      </Typography>
                      <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#16a34a" }}>
                        ₹{(t.summary?.paymentDone || 0).toLocaleString()}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        background: isDark ? "#333" : "#f9fafb",
                        p: "12px",
                        borderRadius: "8px",
                        textAlign: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: "12px", color: isDark ? "#a0a0a0" : "#64748b" }}>
                        Pending
                      </Typography>
                      <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#dc2626" }}>
                        ₹{(t.summary?.pendingAmount || 0).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Items Table */}
                  <Box
                    component="table"
                    sx={{
                      width: "100%",
                      borderCollapse: "collapse",
                      mb: "15px",
                      "& th": {
                        textAlign: "left",
                        p: "10px",
                        background: isDark ? "#333" : "#f1f5f9",
                        color: isDark ? "#e0e0e0" : "#334155",
                        fontSize: "14px",
                        fontWeight: 600,
                      },
                      "& td": {
                        p: "10px",
                        borderBottom: isDark ? "1px solid #444" : "1px solid #f1f5f9",
                        color: isDark ? "#e0e0e0" : "#334155",
                        fontSize: "14px",
                      },
                    }}
                  >
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Weight (g)</th>
                        <th>Rate (₹)</th>
                        <th>Wastage (%)</th>
                        <th style={{ textAlign: "right" }}>Total (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {t.items?.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.name}</td>
                          <td>{item.weight}</td>
                          <td>₹{item.metalRate}</td>
                          <td>{item.wastage}%</td>
                          <td style={{ textAlign: "right" }}>₹{item.total?.toLocaleString() || '0'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Box>

                  {/* Payment Details Footer */}
                  <Box
                    sx={{
                      background: isDark ? "#333" : "#f9fafb",
                      p: "12px",
                      borderRadius: "8px",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "15px",
                      fontSize: "14px",
                      color: isDark ? "#e0e0e0" : "#334155",
                    }}
                  >
                    {/* Regular Payment Methods */}
                    {getAllPaymentMethods(t).map(method => {
                      const amount = getPaymentAmountByMethod(t, method);
                      if (amount > 0) {
                        return (
                          <Box key={method}>
                            <strong>{method.charAt(0).toUpperCase() + method.slice(1)}:</strong>
                            ₹{amount.toFixed(2)}
                          </Box>
                        );
                      }
                      return null;
                    })}

                    {/* Advance Payment Details */}
                    {hasAdvancePayment && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Box>
                          <strong>Advance Payment:</strong> ₹{advancePayment.total.toFixed(2)}
                        </Box>
                        {advancePayment.breakup && Object.keys(advancePayment.breakup).length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '12px', ml: '10px' }}>
                            {Object.entries(advancePayment.breakup).map(([method, amount]) => (
                              <Box key={method}>
                                {method}: ₹{amount === "" ? "0.00" : parseFloat(amount).toFixed(2) || '0.00'}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}

                    <Box>
                      <strong>Discount:</strong> ₹{t.paymentDetails?.discount || 0}
                    </Box>

                    {/* GST Details */}
{(t.summary?.cgst || t.summary?.sgst || t.summary?.igst) && (
  <>
    {/* Check if IGST is present (for inter-state transactions) */}
    {t.summary?.igst ? (
      <Box>
        <strong>IGST:</strong> ₹{t.summary?.igst?.toFixed(2) || 0}
      </Box>
    ) : (
      <>
        {/* Fallback to CGST/SGST for intra-state transactions */}
        {t.summary?.cgst ? (
          <Box>
            <strong>CGST:</strong> ₹{t.summary?.cgst?.toFixed(2) || 0}
          </Box>
        ) : null}
        {t.summary?.sgst ? (
          <Box>
            <strong>SGST:</strong> ₹{t.summary?.sgst?.toFixed(2) || 0}
          </Box>
        ) : null}
      </>
    )}
  </>
)}
                  </Box>

                  {/* Total Payment Summary */}
                  <Box
                    sx={{
                      mt: "12px",
                      pt: "12px",
                      borderTop: isDark ? "1px solid #444" : "1px solid #e5e7eb",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: "14px", color: isDark ? "#e0e0e0" : "#334155" }}>
                      Total Payment: ₹{(t.summary?.paymentDone || 0).toFixed(2)}
                    </Typography>
                    {hasAdvancePayment && (
                      <Typography sx={{ fontSize: "13px", color: "#8b5cf6" }}>
                        (Includes ₹{advancePayment.total.toFixed(2)} advance)
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </>
        )}

        {/* Close Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "15px" }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              borderColor: isDark ? "#555" : "#d1d5db",
              color: isDark ? "#e0e0e0" : "#374151",
              "&:hover": {
                borderColor: isDark ? "#777" : "#9ca3af",
                backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
              },
            }}
          >
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TransactionDetailsModal;